import React from 'react'
import { useSearchParam } from 'react-use'
import styled, { css } from 'styled-components'

import { down, up } from '@Globals/Style/breakpoints'
import { sectionInner, sectionOuter } from '@Globals/Style/mixins'
import { useTranslation } from '@Globals/Translations'

import { useCoupon } from './useCoupon'

interface WrapperProps extends Pick<CouponBarProp, 'position'> {
  color?: string
  textColor?: string
}

const Wrapper = styled.div<WrapperProps>`
  ${sectionOuter}

  ${down('desktop')} {
    background-color: ${({ theme }) => theme.colors.greyDark};
  }

  .inner {
    position: relative;
    ${sectionInner}
  }

  ${props => props.position === 'inside' && css`
    padding-left: 0;
    padding-right: 0;
  `}
`

const Bar = styled.div<Pick<CouponBarProp, 'position'>>`
  background-color: ${({ theme }) => theme.colors.greyDark};
  padding: 15px;
  width: 100%;
  color: #fff;
  text-align: center;
  font-size: .875rem;

  ${up('desktop')} {
    margin-top: 20px;
    margin-bottom: 10px;
    ${props => props.position === 'inside' && css`
      margin-top: 0;
    `}
  }
`

interface CouponBarProp {
  influencerActivationCode?: string
  position?: 'outside' | 'inside'
  thcode: string
}

const CouponBarMarkup = ({ influencerActivationCode, position, thcode }: CouponBarProp) => {
  const { t } = useTranslation()

  const { data } = useCoupon(thcode || '', influencerActivationCode || '')

  if (!data) {
    return null
  }

  const key = data?.coupon?.isAvailable ? 'DISCOUNT_AUTOMATICALLY_APPLIED_IN_CART' : 'UNFORTUNATELY_THIS_OFFER_HAS_EXPIRED_OR_IS_INVALID'

  return (
    <Wrapper position={position}>
      <div className="inner">
        <Bar position={position}>{t(`common:${key}`)}</Bar>
      </div>
    </Wrapper>
  )
}

const CouponBar = ({ position }: Omit<CouponBarProp, 'thcode' | 'influencerActivationCode'>) => {
  const thcode = useSearchParam('thcode')
  const influencerActivationCode = useSearchParam('infactivation')

  if (thcode) {
    return <CouponBarMarkup influencerActivationCode={influencerActivationCode!} position={position} thcode={thcode} />
  }

  return null
}

export default CouponBar
